<template>
  <Modal
    v-model="modalVisible"
    :cancelText="numEligibleUsers ? 'cancel' : ''"
    :handleMainAction="handleMainAction"
    headerText="Reset TOTP MFA on Users"
    :mainActionText="numEligibleUsers ? 'reset' : 'ok'"
  >
    <div v-if="numEligibleUsers">
      <p v-if="!item">
        {{ numEligibleUsers }} of {{ selectedItems.length }} selected users
        have TOTP MFA configured and are eligible for reset.
      </p>
      <p>
        Resetting TOTP Multi-factor Authentication clears existing TOTP configuration
        on users. Reset users won’t be required to verify, but will be prompted
        to reset their TOTP MFA every time they log in to the User Portal
        during their enrollment period.
      </p>
      <p>
        Enrollment periods don’t apply to conditional access policies.
        Instead, users are required to set up MFA the first time they log
        in after a conditional access policy is enabled.
      </p>
      <MFAEnrollmentInput
        v-model="mfaEnrollmentValue"
        keyword="this reset"
      />
    </div>
    <div v-else>
      <p>
        Selected users do not have an active MFA setup eligible for reset.
      </p>
    </div>
  </Modal>
</template>

<script>
import { get } from 'lodash';
import { mapActions, mapGetters } from 'vuex';
import { notify } from 'client/util';
import Constants from 'client/util/Constants';
import MFAEnrollmentInput from 'components/Modal/mfaModals/MFAEnrollmentInput.vue';
import mfaEnrollmentMixin from 'components/mixins/mfaEnrollmentMixin';
import mfaModalMixin from 'components/Modal/mfaModals/mfaModalMixin';
import pluralize from 'pluralize';

const { error, success } = Constants;

export default {
  name: 'UserResetMFAModal',

  components: {
    MFAEnrollmentInput,
  },

  mixins: [
    mfaEnrollmentMixin,
    mfaModalMixin,
  ],
  props: {
    /**
     * A single user's id whose MFA can be reset.
     */
    item: {
      type: Object,
      default: undefined,
    },
  },

  computed: {
    ...mapGetters('SystemUserCollection', {
      getSelectedItems: 'getSelectedItems',
      ineligibleUsers: 'getUsersIneligibleForMFAReset',
    }),

    /**
     * @todo Remove this function once the Vue implementation of UserDetailsAsidePanel releases.
     * This is the same function found in the usr model, we need this here because this component
     * is rendered in the backbone UserDetailsAside panel.
     */
    displayName() {
      const { item } = this;
      const firstname = get(item, 'firstname', '');
      const lastname = get(item, 'lastname', '');
      const username = get(item, 'username', '').trim();
      const displayName = `${firstname} ${lastname}`.trim();

      return displayName || username || '';
    },

    numEligibleUsers() {
      return this.selectedItems.length - this.ineligibleUsers.length;
    },

    selectedItems() {
      const { item } = this;
      return item ? [get(item, '_id')] : this.getSelectedItems;
    },
  },

  methods: {
    ...mapActions('SystemUserModel', [
      'setItemField',
      'setPreviousItemField',
    ]),

    ...mapActions('SystemUserCollection', [
      'bulkResetMFA',
      'unselectItems',
    ]),

    /**
     * Executes when the user clicks the main action button in the modal.
     * Validates the MFA enrollment period value prior to resetting MFA
     * for the selected users. If there are users that cannot have MFA
     * reset, these users are unselected.
     */
    async handleMainAction() {
      if (this.v2Errors.items.length) {
        notify(error, 'Please provide a valid enrollment period value.');
      } else {
        // Don't take any action if there aren't eligible users
        if (this.numEligibleUsers) {
          if (this.ineligibleUsers.length) {
            this.unselectItems({ ids: this.ineligibleUsers.map(item => item.id) });
          }

          if (this.item) {
            await this.resetMFAForSingleUser();
          } else {
            await this.resetMFAForUsers();
          }
        }

        /**
         * Close the modal and reset the mfa enrollment value to the default
         * for the next time the modal is displayed.
         */
        this.hideModal();
        this.resetMfaEnrollmentValue();
      }
    },

    /**
     * Action for resetting a User's Mfa in a user details aside panel. Updates both the
     * SystemUserCollection and the SystemUserModel upon a successful request.
     * @return {Object<Promise>} Returns the promise from the action
     */
    resetMFAForSingleUser() {
      const { item } = this;
      const fullName = this.displayName;
      const payload = {
        exclusionDays: this.exclusionDays(),
        exclusionUntil: this.getExclusionUntil(),
        ids: [get(item, '_id')],
      };
      // Reuse the bulkMFA action so we do not have to set the SystemUserCollection state on success
      return this.bulkResetMFA(payload)
        .then((response) => {
          const resetSuccess = get(response, 'numSuccess');
          let notificationType = error;
          let notificationMessage = `MFA could not be reset for ${fullName}.`;
          if (resetSuccess) {
            notificationType = success;
            notificationMessage = `MFA has been reset for ${fullName}.`;
            this.updateSystemUserModel(payload);
          }
          return notify(notificationType, notificationMessage);
        });
    },

    /**
     * Resets MFA for the selected users. Notifies user of success or failure.
     */
    resetMFAForUsers() {
      const numUsers = this.selectedItems.length;
      return this.bulkResetMFA({
        exclusionDays: this.exclusionDays(),
        exclusionUntil: this.getExclusionUntil(),
        ids: this.selectedItems,
      })
        .then((response) => {
          const usersNoun = pluralize('user', numUsers);
          // Make the default notification an error
          let notificationType = error;
          let notificationMessage = `MFA could not be reset for the selected ${usersNoun}.`;
          // Upon success, set the notification parameters to different values
          if (get(response, 'numSuccess')) {
            notificationType = success;
            notificationMessage = (numUsers === 1)
              ? 'MFA successfully reset for the selected user.'
              : `MFA successfully reset for ${response.numSuccess} of ${numUsers} users.`;
            this.unselectItems({ ids: this.selectedItems });
          }
          return notify(notificationType, notificationMessage);
        });
    },

    /**
     * After a successful mfa reset, we need to update the SystemUserModel. We get the
     * mfaEnrollmentValue from the form data in the mfaModalMixin.
     *
     * @todo Update this when the saveItemField action is created in the SystemUserModel so we do
     * not have to make multple saveItemField calls to the SystemUserModel store.
     */
    updateSystemUserModel() {
      const mfaPayload = {
        key: 'mfa',
        value: this.getMfaPayload(this.item.state),
      };

      this.setItemField(mfaPayload);
      this.setPreviousItemField(mfaPayload);
    },
  },
};
</script>
